import { EntitiesFilter } from './EntitiesFilter';
import { Contatto } from './Contatto';
import { Destinazione } from './Destinazione';
import { Automezzo } from './Automezzo';
import { FunzionalitaSelezionata } from './Funzionalita';

/**
 * Collezione di dati su un intervento che sta
 * venendo compilato nell'applicazione
 */
export interface StatoInterventoAperto {
    datiTestata: TestataIntervento;
    /**
     * True se c'è un disallinamento rispetto ai dati su server (cioè
     * una modifica da sincronizzare) sui dati di TESTATA
     */
    isTestataDirty: boolean;
    /**
     * True se c'è un disallinamento rispetto ai dati su server (cioè 
     * una modifica da sincronizzare) sugli EVENTI di manodopera
     */
    areEntitaDirties: boolean;
    /**
     * Quando e' stata fatta l'ultima modifica
     */
    dataModifica: Date;
}

export interface TestataInterventoResponse {
    SDTwsrinterventi: TestataIntervento[],
    st: number,
    messer: string
}
/**
 * Modellazione dell'entità intervento che rispecchia
 * la struttura di integra (con campi transcodificati
 * in descrizioni parlanti)
 */
export interface TestataIntervento {
    /**
     * (Mvtid) id univoco che compone la chiave primaria assieme al codice
     * dell'azienda
     */
    idIntervento: number;
    /**
     * (mvt_num)
     */
    numeroDocumento: string;
    /**
     * Identificativo unnivoco dell'azienda; parte della chiave primaria
     */
    aziCdazi: string;
    /**
     * (cliRgsoc)
     */
    ragioneSociale: string;
    /**
     * (mvt_clacod)
     * @since 2.6.0
     */
    codiceCliente: number;
    /**
     * (mvt_clatipo)
     * @since 2.6.0
     */
    tipologiaCliente: string;

    /**
     * (MvtComcod) la commessa e' usata su integra
     * per rappresentare il concetto di macchinario
     */
    codiceCommessa: string;
    /**
     * (MvtComid)
     */
    idCommessa: number;
    /**
     * (MvtComdescr)
     */
    descrizioneCommessa: string;
    /**
     * (mvtProblema)
     */
    descrizioneProblema: string;
    /**
     * (MvtDtmov)
     */
    dataIntervento: Date;
    /**
     * (dataFineIntervento)
     */
    dataFineIntervento: Date;
    /**
     * (oraInizioIntervento)
     */
    oraInizioIntervento: number;
    /**
     * (minutiInizioIntervento)
     */
    minutiInizioIntervento: number;
    /**
     * (oraFineIntervento)
     */
    oraFineIntervento: number;
    /**
     * (minutiFineIntervento)
     */
    minutiFineIntervento: number;
    /**
     * (Noteint)
     */
    noteIntervento: string;
    /**
     * (noteattsvolta) note sull'intervento segnalate dal tecnico (in chiusura o non)
     */
    noteDiAttivita: string;
    /**
     * (MvtFlggaranzia)
     */
    flagGaranzia: number;
    /**
     * (mvr_przvaltrasforfait) e' il diritto di chiamata, cioe' un costo fisso da
     * corrispondere sempre
     */
    costoForfaittario: number;
    /**
     * (MvrQtatrasforfait) numero di diritti di chiamate compresi nell'intervento
     * nel caso in cui l'intervento coinvola piu' persone
     */
    quantitaForfait: number;
    /**
     * (MvrQtatrasfkm)
     */
    kilometriTrasferta: number;
    /**
     * (MvrPrzvaltrasfkm) costo per kilometri percorsi
     */
    costoAlKilometro: number;
    /**
     * (MvrPrzvaltraasfauto) costi di casello, etc
     */
    speseAutostradali: number;
    /**
     * (mvr_qtatrasfauto)
     */
    quantitaSpeseAutostradali: number;
    /**
     * (MvrNotetrasfvitto)
     */
    noteTrasferta: string;
    /**
     * (MvrPrzvaltraasfauto)
     */
    speseVittoAlloggio: number;
    /**
     * (mvr_qtatrasfvitto)
     */
    quantitaVittoAlloggio: number;
    /**
     * Codice dello stato attuale dell'intervento.
     * È una stringa perché i codici possono variare in base alla configurazione.
     * 
     * Ad oggi (20/10/2026) i codici sono:
     * - 10: Da pianificare
     * - 15: Assegnato
     * - 20: Pianificato
     * - 25: Ripianificato
     * - 30: Eseguito
     * - 50: Sospeso
     * - 80: KO
     * - 85: Annullato
     * - 90: Chiuso/archiviato da amministrazione
     */
    codiceStatoIntervento: string;

    // Veicolo dei dati necessari alla chiusura
    firmaChisuraClienteBlob: string;
    firmatarioCliente: string;
    interessatiAMail: Contatto[];
    /**
     * Nome del tipo di report da applicare per
     * generare il documento "rapportino"
     * @since 2.6.1
     */
    tipoReportScelto: string;
    /**
     * Per ciascun cliente è possibile individuare
     * un elenco di tipologie di stampe utilizzabili
     * al momento della creazione del report conclusivo
     * @since 2.6.1
     */
    tipiReportCompatibili: TipoReport[];
    /**
     * Base64 del rapportino dell'intervento
     * @since 3.0.0
     */
    base64: string;
    /** DATI CLIENTE */
    /**
     * Indirizzo cliente
     * @since 3.0.0
     */
    indirizzo: string;
    /**
     * CAP cliente
     * @since 3.0.0
     */
    cap: string;
    /**
     * Città cliente
     * @since 3.0.0
     */
    citta: string;
    /**
     * Provincia cliente
     * @since 3.0.0
     */
    provincia: string;
    /**
     * Numero telefono
     * @since 3.0.0
     */
    telefono: string;
    /**
    * Indirizzo emial
    * @since 3.0.3
    */
    mail: string;
    /**
    * Latitudine per geocalizzazione
    * @since 3.0.0
    */
    latitudine: string;
    /**
     * Longitudine per geocalizzazione
     * @since 3.0.0
     */
    longitudine: string;
    /**
     * Dati destinazione
     * @since 3.0.3
     */
    destinazione: Destinazione;
    /**
     * Automezzi prenotati per un determinato intervento
     */
    automezziPrenotati?: Automezzo[]
    /**
     * Funzionalità associate all'intervento
     */
    funzionalita?: FunzionalitaSelezionata[];
    /**
     * Tipo documento dell'intervento
     */
    tipoDocumento?: string;
    /**
     * Stato dell'intervento restituito dal WS (Da pianficiare = IDP, Pianificato = IPI ecc..)
     */
    mvsStatoIntervento?: string;
    /**
     * Motivazione KO già associata all'intervento
    */
    motivazioneKO?: string;
}

export interface TipoReport {
    codice: string;
    descrizione: string;
}

/**
 * Raccolta di dati che:
 * - Governa il filtraggio della relativa anagrafica di testata
 * interventi mostrata sulla relativa pagina  
 * - DEVE essere scambiata tra client
 * e server nelle richieste di ottenimento dei dati delle 
 * testate di intervento
 */
export class InterventiFilterData extends EntitiesFilter {
    /**
     * Filtro, con prelazione sugli altri, valorizzato solo
     * quando si vuole ottenere il dettaglio di 1 intervento
     * NB: attualmente non utlizzato
     */
    filtroIdIntervento?: number;
    /**
     * Filtro per numero intervento
     */
    filtroNumeroIntervento?: string;
    /**
     * Combinazione dei codici di stato che l'utente vuole visualizzare.
     * Gli stati disponibili sono configurati tramite FLTRSTINT.
     *
     * Esempi:
     * 10,30
     * 20
     * 10,20,30,90
     */
    filtroStatoIntervento = '20';
    filtroRagioneSociale = '';
    filtroCodiceCommessa = '';
    filtroDaDataInterventoIsoString: string;
    filtroADataInterventoIsoString: string;

    public reset(): void {
        this.filtroIdIntervento = null;
        this.filtroNumeroIntervento = "";
        this.filtroRagioneSociale = '';
        this.filtroCodiceCommessa = '';
        this.filtroStatoIntervento = '20';
        this.filtroDaDataInterventoIsoString = '';
        this.filtroADataInterventoIsoString = '';
    }
}

export interface CreaInterventoRequest {
    aziCdazi: string;
    codiceAddetto: string;
    tipologiaCliente: string;
    codiceTipoDocumento: string;
    // ClaCod
    codiceCliente: number;
    idCommessa: number;
    motivazione: string;
    dataIntervento: Date;
    dataFineIntervento: Date;
    oraInizioIntervento: number;
    minutiInizioIntervento: number;
    oraFineIntervento: number;
    minutiFineIntervento: number;
    flagInGaranzia: boolean;
    codiciAutomezzi?: string[];
    funzionalita?: FunzionalitaSelezionata[];
}

export interface EsitoCreaIntevento {
    st: number;
    idInterventoCreato: number;
    progressivoIntervento: number; // Secondo pezzo della chiave
    numeroIntervento: string;
    messerr: string;
}

export interface InterventoUpdResponse {
    st: number,
    messerr: string,
}

export interface Base64DocumentodResponse {
    st: number,
    filebase64: string,
}

export interface OpzioneCambioStato {
    codiceDestinazione: string;
    etichetta: string;
    funzione: string;
}

export interface OpzioneMotivazioneKO {
    codice: string;
    descrizione: string;
}
